import EventHubMixin from '@/mixins/EventHub'
import TimeMixin from '@/mixins/Time'
import PathForMixin from '@/mixins/PathFor'
import { TweenLite } from 'gsap'
import { Howl, Howler } from 'howler'

export default
{
    name: 'app-sound',

    mixins:
    [
        EventHubMixin,
        TimeMixin,
        PathForMixin
    ],

    watch:
    {
        $route()
        {
            const ambientName = this.$route.meta.soundAmbient

            if(ambientName !== this.audios.ambientName)
            {
                this.audios.ambientName = ambientName

                if(ambientName === 'default')
                {
                    this.audios.all.ambientDefault.fade(0, 1, 1500)
                    this.audios.all.ambientUnder.fade(1, 0, 1500)
                    this.$eventHub.$emit('sound.play', 'woosh')
                }
                else
                {
                    this.audios.all.ambientDefault.fade(1, 0, 1500)
                    this.audios.all.ambientUnder.fade(0, 1, 1500)
                }
            }

            this.$eventHub.$emit('sound.play', 'click')
        }
    },

    created()
    {
        // Set up
        const localStorageMusic = window.localStorage.getItem('music', '1')

        this.playing = !localStorageMusic || localStorageMusic !== '0'
        this.volume = this.playing ? 1 : 0

        // Volume tween
        this.volumeTween = TweenLite.fromTo(
            this,
            1,
            {
                volume: 0
            },
            {
                volume: 1,
                paused: true,
                onUpdate: () =>
                {
                    Howler.volume(this.volume)
                }
            }
        )

        Howler.volume(this.volume)
    },

    mounted()
    {
        this.setAudios()
        this.setWaves()
    },

    destroyed()
    {
        Howler.unload()
    },

    methods:
    {
        setAudios()
        {
            this.audios = {}

            this.audios.ambientName = this.$route.meta.soundAmbient

            this.audios.all = {}
            this.audios.all.ambientDefault = new Howl({ autoplay: true, loop: true, volume: this.$route.meta.soundAmbient === 'default' ? 1 : 0, src: this.pathFor('/audio/1-ORANO XP-Boucle musique principale.mp3') })
            this.audios.all.ambientUnder = new Howl({ autoplay: true, loop: true, volume: this.$route.meta.soundAmbient === 'under' ? 1 : 0, src: this.pathFor('/audio/2-ORANO XP-Boucle musique light.mp3') })
            this.audios.all.keypoint = new Howl({ volume: 0.5, src: this.pathFor('/audio/6-ORANO XP-SD HOTSPOT.mp3') })
            this.audios.all.pushButton = new Howl({ volume: 0.6, src: this.pathFor('/audio/4-ORANO XP-SD EXPLORE Loading barre V3.mp3') })
            this.audios.all.click = new Howl({ volume: 0.8, src: this.pathFor('/audio/5-ORANO XP-SD EXPLORE Clic.mp3') })
            this.audios.all.valid = new Howl({ src: this.pathFor('/audio/7-ORANO XP SD Valid.mp3') })
            this.audios.all.transition = new Howl({ volume: 0.5, src: this.pathFor('/audio/9-ORANO XP-SD Chgt DECOR.mp3') })
            this.audios.all.woosh = new Howl({ src: this.pathFor('/audio/8-ORANO XP-SD Zoom back home.mp3') })

            // Ambient play event
            // Fade up volume
            this.audios.all.ambientDefault.once('play', () =>
            {
                if(this.playing)
                {
                    this.volumeTween.play()
                }
            })

            this.$eventHub.$on('sound.play', (_soundName) =>
            {
                const sound = this.audios.all[_soundName]

                if(sound)
                {
                    sound.seek(0)
                    sound.play()
                }
            })
        },

        play()
        {
            this.playing = true

            this.volumeTween.play()

            window.localStorage.setItem('music', '1')
        },

        pause()
        {
            this.playing = false

            this.volumeTween.reverse()

            window.localStorage.setItem('music', '0')
        },

        setWaves()
        {
            this.waves = {}
            this.waves.time = 0
            this.waves.$canvas = this.$refs.canvas
            this.waves.context = this.waves.$canvas.getContext('2d')
            this.waves.width = this.waves.$canvas.width
            this.waves.height = this.waves.$canvas.height
            this.waves.speed = {}
            this.waves.speed.min = 0.0035
            this.waves.speed.max = 0.008
            this.waves.speed.current = this.waves.speed.min
            this.waves.points = {}
            this.waves.points.amplitude = 1
            this.waves.points.count = Math.ceil(this.waves.width / this.waves.points.amplitude)
            this.waves.points.items = []

            // Create gradient
            this.waves.gradient = this.waves.context.createLinearGradient(0, 0, this.waves.width, 0)
            this.waves.gradient.addColorStop(0, '#000000')
            this.waves.gradient.addColorStop(0.1, '#ffffff')
            this.waves.gradient.addColorStop(0.9, '#ffffff')
            this.waves.gradient.addColorStop(1, '#000000')

            for(let i = 0; i < this.waves.points.count + 1; i++)
            {
                const point = {}
                point.x = i * this.waves.points.amplitude
                point.y = Math.round(this.waves.height * 0.5) + 0.5

                this.waves.points.items.push(point)
            }
        },

        updateWaves()
        {
            // Clear canvas
            this.waves.context.clearRect(0, 0, this.waves.width, this.waves.height)

            this.waves.time += this.time.delta * this.waves.speed.current

            // Set style
            this.waves.context.lineWidth = 1
            this.waves.context.strokeStyle = this.waves.gradient

            // Start drawing
            this.waves.context.beginPath()

            // Draw each point
            for(let i = 0; i < this.waves.points.items.length; i++)
            {
                const point = this.waves.points.items[i]

                point.y = Math.sin(this.waves.time   + point.x * 0.3) * this.waves.height * 0.5 // Sinus
                point.y *= 0.1 + this.volume * 0.9
                point.y *= 0.8 + Math.sin(this.time.elapsed * 0.00741) * 0.1 + Math.sin(this.time.elapsed * 0.005) * 0.1
                point.y *= Math.sin(point.x / this.waves.width * Math.PI) // Side attenuation
                point.y += this.waves.height * 0.5 // Centering

                if(i === 0)
                {
                    this.waves.context.moveTo(point.x, point.y)
                }
                else
                {
                    this.waves.context.lineTo(point.x, point.y)
                }
            }

            // Draw
            this.waves.context.stroke()
        },

        onTick()
        {
            this.updateWaves()
        },

        onMouseOver()
        {
            this.waves.speed.current = this.waves.speed.max
        },

        onMouseLeave()
        {
            this.waves.speed.current = this.waves.speed.min
        },

        onClick()
        {
            // // If not iOS, try to play anyway
            // if(this.$root.config.platform.os !== 'iOS')
            // {
            //     this.$audio.play()
            // }

            if(this.playing)
            {
                this.pause()
            }
            else
            {
                this.play()
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-sound/script.js